import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { currentUserPreferencesSelector } from 'store/slices/userDetailsSlice';
import { useUpdateUserPreferencesMutation } from 'store/apis/userDetailsApi';
import { SLIDE_PANEL_COLLAPSED_FIELD } from 'components/ImageViewer/constants';
import { KEYS } from 'constants/keyboard';

const isTypingTarget = (target) => {
  const tag = target?.tagName;

  return tag === 'INPUT' || tag === 'TEXTAREA' || !!target?.isContentEditable;
};

/**
 * Whether the metadata/comments panel next to the slide is collapsed.
 *
 * The choice is a user preference, so it survives reloads and slide changes,
 * and `]` toggles it from anywhere on the page (except while typing).
 */
export const useSlidePanelCollapsed = () => {
  const preferences = useSelector(currentUserPreferencesSelector);
  const [updateUserPreferences] = useUpdateUserPreferencesMutation();
  const isCollapsed = !!preferences[SLIDE_PANEL_COLLAPSED_FIELD];

  const toggle = useCallback(
    () =>
      updateUserPreferences({
        postParams: { [SLIDE_PANEL_COLLAPSED_FIELD]: !isCollapsed },
        partial: true,
      }),
    [isCollapsed, updateUserPreferences],
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key !== KEYS.BRACKET_RIGHT ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isTypingTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      toggle();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggle]);

  return { isCollapsed, toggle };
};
